/**
 * Global theme behaviour shared across sections:
 * - scroll reveal for [data-reveal] elements
 * - header text color swap based on the [data-ground] section in view
 * - a small cart helper (AJAX add-to-cart + toast + header count)
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function luminance(hex) {
    hex = (hex || '').replace('#', '');
    if (hex.length !== 6) return 255;
    var r = parseInt(hex.substr(0, 2), 16);
    var g = parseInt(hex.substr(2, 2), 16);
    var b = parseInt(hex.substr(4, 2), 16);
    return 0.299 * r + 0.587 * g + 0.114 * b;
  }

  function initGroundSwap() {
    var sections = Array.prototype.slice.call(document.querySelectorAll('[data-ground]'));
    if (!sections.length) return;

    var apply = function (ground) {
      var light = luminance(ground) > 140;
      document.documentElement.style.setProperty('--nav-fg', light ? 'var(--color-ink)' : 'var(--color-cream)');
      document.documentElement.style.setProperty(
        '--nav-border',
        light ? 'rgb(20 27 45 / 0.14)' : 'rgb(255 255 255 / 0.14)'
      );
    };

    apply(sections[0].getAttribute('data-ground'));

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) apply(entry.target.getAttribute('data-ground'));
        });
      },
      { rootMargin: '-50% 0px -50% 0px', threshold: 0 }
    );
    sections.forEach(function (section) {
      io.observe(section);
    });
  }

  function initScrollReveal() {
    var reveals = Array.prototype.slice.call(document.querySelectorAll('[data-reveal]'));
    if (!reveals.length || reduceMotion) return;

    var vh = window.innerHeight || document.documentElement.clientHeight;
    var toObserve = [];

    reveals.forEach(function (el) {
      var inView = el.getBoundingClientRect().top < vh * 0.92;
      if (inView) return;
      el.style.opacity = '0';
      el.style.transform = 'translateY(24px)';
      el.style.transition = 'opacity 0.6s ease, transform 0.6s cubic-bezier(0.4,0,0.2,1)';
      toObserve.push(el);
    });

    if (!toObserve.length) return;

    var ro = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'none';
            ro.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );
    toObserve.forEach(function (el) {
      ro.observe(el);
    });
  }

  var toastTimer;
  function showToast(message) {
    var toast = document.querySelector('[data-cart-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('cart-toast--visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toast.classList.remove('cart-toast--visible');
    }, 2600);
  }

  function updateCartCount(count) {
    document.querySelectorAll('.js-cart-count').forEach(function (el) {
      el.textContent = count;
      el.hidden = count <= 0;
    });
  }

  function refreshCartCount() {
    fetch('/cart.js')
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        updateCartCount(cart.item_count);
      })
      .catch(function () {});
  }

  /**
   * Adds an item to the cart via the AJAX Cart API.
   * @param {{id: (string|number), quantity?: number}} payload
   * @param {string} [successMessage]
   * @returns {Promise<object>}
   */
  function addToCart(payload, successMessage) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: payload.id, quantity: payload.quantity || 1, properties: payload.properties }),
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw data;
          return data;
        });
      })
      .then(function (item) {
        refreshCartCount();
        showToast(successMessage || (item.product_title || item.title) + ' added to bag');
        document.dispatchEvent(new CustomEvent('cart:added', { detail: item }));
        return item;
      })
      .catch(function (error) {
        showToast((error && error.description) || 'Could not add item to bag');
        throw error;
      });
  }

  window.Belgravia = window.Belgravia || {};
  window.Belgravia.addToCart = addToCart;
  window.Belgravia.showToast = showToast;
  window.Belgravia.refreshCartCount = refreshCartCount;

  function initQuickAdd() {
    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-quick-add]');
      if (!button || button.disabled) return;
      event.preventDefault();
      var originalText = button.textContent;
      button.disabled = true;
      addToCart(
        { id: button.getAttribute('data-variant-id'), quantity: button.getAttribute('data-quantity') || 1 },
        button.getAttribute('data-success-message')
      ).finally(function () {
        button.disabled = false;
        button.textContent = originalText;
      });
    });
  }

  function initCarousels() {
    document.querySelectorAll('[data-carousel]').forEach(function (carousel) {
      var track = carousel.querySelector('[data-carousel-track]');
      var prev = carousel.querySelector('[data-carousel-prev]');
      var next = carousel.querySelector('[data-carousel-next]');
      if (!track) return;
      var scrollAmount = function () {
        var firstItem = track.firstElementChild;
        return firstItem ? firstItem.getBoundingClientRect().width + 28 : track.clientWidth * 0.8;
      };
      if (prev) prev.addEventListener('click', function () {
        track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
      });
      if (next) next.addEventListener('click', function () {
        track.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
      });
    });
  }

  function initAccordions() {
    document.querySelectorAll('[data-accordion-trigger]').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var item = trigger.closest('[data-accordion-item]');
        if (!item) return;
        var isOpen = item.getAttribute('data-open') === 'true';
        item.setAttribute('data-open', isOpen ? 'false' : 'true');
        trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      });
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initGroundSwap();
    initScrollReveal();
    initQuickAdd();
    initCarousels();
    initAccordions();
    refreshCartCount();
  });
})();
